import { useCallback, useEffect, useRef, useState } from 'react';
import { Editor } from '@tiptap/core';
import { closeHistory } from '@tiptap/pm/history';
import { TextSelection } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Dialog } from '../dialogs/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { defaultFieldLayout, FieldLayout, normalizeFieldLayout, validLayoutSize } from '../../general/field-container-layout';
import { selectedContainerFields } from './field-container';

export function FieldContainerDialog({ editor, request }: { editor: Editor; request: number }) {
    const [draft, setDraft] = useState<FieldLayout | null>(null);
    const [error, setError] = useState('');
    const handled = useRef(0);
    const context = useRef<{ doc: typeof editor.state.doc; from: number; to: number; existing: boolean; fields: PMNode[] | null } | null>(null);
    const open = useCallback((position?: number) => {
        const from = position ?? editor.state.selection.from;
        const node = editor.state.doc.nodeAt(from);
        const existing = position !== undefined && node?.type.name === 'fieldContainer';
        const fields = existing ? [] : selectedContainerFields(editor);
        context.current = { doc: editor.state.doc, from, to: existing ? from + node!.nodeSize : editor.state.selection.to, existing, fields };
        setDraft(existing ? normalizeFieldLayout(node!.attrs) : { ...defaultFieldLayout });
        setError(fields === null ? 'Select only form fields to move them into a container. Quiz fields and ordinary content cannot be included.' : '');
    }, [editor]);
    useEffect(() => {
        setDraft(null); handled.current = 0;
        if (!editor.storage.fieldContainer) return;
        editor.storage.fieldContainer.onEdit = open;
        return () => { editor.storage.fieldContainer.onEdit = null; };
    }, [editor, open]);
    useEffect(() => { if (request && request !== handled.current && !editor.isDestroyed) { handled.current = request; open(); } }, [request, editor, open]);
    function save() {
        if (!draft || !context.current) return;
        if (context.current.fields === null) {
            setError('Select only form fields to move them into a container. Quiz fields and ordinary content cannot be included.'); return;
        }
        for (const key of ['width', 'labelWidth', 'inputWidth', 'textareaHeight', 'columnGap', 'rowGap'] as const) {
            if (!validLayoutSize(draft[key].trim(), key === 'labelWidth', key === 'textareaHeight')) {
                setError('Sizes must use px, %, rem, em or ch. Label width also accepts auto; heights may be blank.'); return;
            }
        }
        if (editor.isDestroyed || !editor.state.doc.eq(context.current.doc)) { setError('The content has changed. Close this dialog and open it again.'); return; }
        const attrs = normalizeFieldLayout(draft);
        const { from, to, existing, fields } = context.current;
        editor.chain().focus().command(({ tr }) => {
            closeHistory(tr);
            if (existing) tr.setNodeMarkup(from, undefined, attrs);
            else {
                const container = editor.schema.nodes.fieldContainer.create(attrs, fields);
                tr.replaceRangeWith(from, to, container);
                tr.doc.descendants((node, pos) => {
                    if (node === container) tr.setSelection(TextSelection.create(tr.doc, pos + 1 + node.content.size));
                });
            }
            return true;
        }).run();
        setDraft(null);
    }
    const change = (key: keyof FieldLayout, value: string | boolean) => setDraft(current => current ? { ...current, [key]: value } : current);
    return <Dialog header={context.current?.existing ? 'Field container properties' : 'Insert field container'} visible={!!draft} modal
        onHide={() => setDraft(null)} style={{ width: '38rem', maxWidth: '95vw' }}
        footer={<><Button label="Cancel" outlined onClick={() => setDraft(null)} /><Button label={context.current?.existing ? 'Save' : 'Insert'} onClick={save} /></>}>
        {draft && <div className="flex flex-column gap-3">
            {error && <div role="alert" className="p-3 border-round" style={{ border: '1px solid var(--red-500)', color: 'var(--red-700)' }}>{error}</div>}
            <p className="mt-0">Defaults for fields inside this container. Individual field attributes take precedence.</p>
            {!!context.current?.fields?.length && <p className="mt-0" role="status">{context.current.fields.length} selected field{context.current.fields.length === 1 ? '' : 's'} will be moved into this container.</p>}
            {([['width', 'Container width'], ['labelWidth', 'Label column width (auto: up to 35%)'], ['inputWidth', 'Input width'],
                ['textareaHeight', 'Textarea height'], ['columnGap', 'Column spacing'], ['rowGap', 'Field spacing']] as const)
                .map(([key, label]) => <div key={key} className="flex align-items-center gap-3"><label htmlFor={'layout-' + key} className="flex-1">{label}</label>
                    <InputText id={'layout-' + key} value={draft[key]} style={{ width: '9rem' }} onChange={event => change(key, event.target.value)} /></div>)}
            <label htmlFor="layout-label-align">Label alignment</label><select id="layout-label-align" className="p-inputtext" value={draft.labelAlign} onChange={event => change('labelAlign', event.target.value)}>
                <option value="left">Left</option><option value="right">Right</option></select>
            <label htmlFor="layout-orientation">Radio and checkbox group orientation</label><select id="layout-orientation" className="p-inputtext" value={draft.orientation} onChange={event => change('orientation', event.target.value)}>
                <option value="vertical">One option per line</option><option value="horizontal">Horizontal (wrap when needed)</option></select>
            <label><input type="checkbox" checked={draft.stackOnMobile} onChange={event => change('stackOnMobile', event.target.checked)} /> Stack labels above inputs on narrow screens</label>
        </div>}
    </Dialog>;
}
